import { z } from 'zod';
import { MemoryEntryPointSchema } from '@terra/shared-contracts-memory';
import { isMongoObjectId, MongoObjectIdSchema } from '@terra/shared-utils';

export const MemoryObjectIdSchema = MongoObjectIdSchema.transform((id) =>
  id.toLowerCase(),
);

export const MemoryCallerGroupSchema = z.enum([
  'neo',
  'agent_tools',
  'copilot',
  'business_context',
  'people',
  'system',
  'other',
]);

export const MemoryItemStatusSchema = z.enum([
  'active',
  'pending',
  'rejected',
  'archived',
]);

export const MemoryArchiveReasonSchema = z.enum([
  'user_deleted',
  'obsolete',
  'merged',
]);

export const MemoryChangeTypeSchema = z.enum([
  'CREATED',
  'MERGED',
  'UPDATED',
  'REWIRED',
  'REVERSED',
  'ARCHIVED',
  'USER_DELETED',
]);

export const MemoryAudienceSchema = z.enum([
  'agent',
  'copilot',
  'shared',
  'attack_learnings',
]);

export const MemoryCreationMethodSchema = z.enum([
  'manual',
  'automatic',
  'system',
]);

export const MemoryEvidenceKindSchema = z.enum([
  'signal_feedback',
  'finding_feedback',
  'execution_span',
  'chat_turn',
  'manual_text',
  'screenshot',
  'legacy_agentcore',
]);

export const MemoryRetrievalSchema = z.enum([
  'list',
  'search',
  'get',
  'count',
  'lookup',
]);

export const MemoryProviderIdSchema = z.enum(['aws', 'gcp', 'owned']);

export const MemoryCockpitRangeSchema = z
  .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
  })
  .refine(({ from, to }) => new Date(from) <= new Date(to), {
    error: 'from must be before or equal to to',
  });

export const MemoryHeadDtoSchema = z.object({
  id: MemoryObjectIdSchema,
  tenantId: z.string(),
  applicationId: z.string(),
  endpointId: z.string().nullable(),
  audience: MemoryAudienceSchema,
  content: z.string(),
  version: z.int().positive(),
  status: MemoryItemStatusSchema,
  archiveReason: MemoryArchiveReasonSchema.nullable(),
  mergedInto: MemoryObjectIdSchema.nullable(),
  entryPoint: MemoryEntryPointSchema,
  creationMethod: MemoryCreationMethodSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const MemoryRunKindStatSchema = z.object({
  runKind: z.string(),
  callerGroup: MemoryCallerGroupSchema,
  reads: z.int().nonnegative(),
  exposures: z.int().nonnegative(),
  runs: z.int().nonnegative(),
  withRunId: z.int().nonnegative(),
  searchReads: z.int().nonnegative(),
  truncatedReads: z.int().nonnegative(),
  lastReadAt: z.iso.datetime().nullable(),
});

export const MemoryOverviewSchema = z.object({
  providerId: MemoryProviderIdSchema,
  statusCounts: z
    .object({ status: MemoryItemStatusSchema, count: z.int().nonnegative() })
    .array(),
  archiveReasonCounts: z
    .object({
      reason: MemoryArchiveReasonSchema,
      count: z.int().nonnegative(),
    })
    .array(),
  changeTypeCounts: z
    .object({
      changeType: MemoryChangeTypeSchema,
      count: z.int().nonnegative(),
    })
    .array(),
  readTotals: z.object({
    agentReads: z.int().nonnegative(),
    peopleViews: z.int().nonnegative(),
  }),
  runKindStats: MemoryRunKindStatSchema.array(),
  dailyCallerReads: z
    .object({
      day: z.iso.date(),
      callerGroup: MemoryCallerGroupSchema,
      reads: z.int().nonnegative(),
    })
    .array(),
});

export const GetMemoryOverviewQuerySchema = MemoryCockpitRangeSchema.and(
  z.object({
    tenantId: z.string().optional(),
    applicationId: z.string().optional(),
  }),
);

export const MemoryListStatusFilterSchema = z.enum([
  'active',
  'archived',
  'all',
]);

export const ListMemoriesQuerySchema = z
  .object({
    status: MemoryListStatusFilterSchema.prefault('active'),
    tenantId: z.string().optional(),
    applicationId: z.string().optional(),
    query: z.string().trim().min(1).optional(),
    cursor: MemoryObjectIdSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).prefault(50),
  })
  .refine(
    ({ query, tenantId }) =>
      !query || isMongoObjectId(query) || Boolean(tenantId),
    { error: 'Text search needs a tenant. Ids work in all tenants.' },
  );

export const ListMemoriesResponseSchema = z.object({
  items: MemoryHeadDtoSchema.array(),
  nextCursor: MemoryObjectIdSchema.nullable(),
});

export const GetMemoryReadCountsBodySchema = MemoryCockpitRangeSchema.and(
  z.object({
    memories: z
      .object({
        tenantId: z.string().min(1),
        memoryId: MemoryObjectIdSchema,
      })
      .array()
      .min(1)
      .max(50),
  }),
);

export const MemoryReadCountsSchema = z.object({
  counts: z
    .object({
      memoryId: MemoryObjectIdSchema,
      reads: z.int().nonnegative(),
      lastReadAt: z.iso.datetime().nullable(),
    })
    .array(),
});

export type MemoryCallerGroup = z.infer<typeof MemoryCallerGroupSchema>;
export type MemoryItemStatus = z.infer<typeof MemoryItemStatusSchema>;
export type MemoryArchiveReason = z.infer<typeof MemoryArchiveReasonSchema>;
export type MemoryChangeType = z.infer<typeof MemoryChangeTypeSchema>;
export type MemoryEvidenceKind = z.infer<typeof MemoryEvidenceKindSchema>;
export type MemoryRetrieval = z.infer<typeof MemoryRetrievalSchema>;
export type MemoryProviderId = z.infer<typeof MemoryProviderIdSchema>;
export type MemoryListStatusFilter = z.infer<
  typeof MemoryListStatusFilterSchema
>;
export type MemoryHeadDto = z.infer<typeof MemoryHeadDtoSchema>;
export type MemoryRunKindStat = z.infer<typeof MemoryRunKindStatSchema>;
export type MemoryOverview = z.infer<typeof MemoryOverviewSchema>;
export type GetMemoryOverviewQuery = z.infer<
  typeof GetMemoryOverviewQuerySchema
>;
export type ListMemoriesQuery = z.infer<typeof ListMemoriesQuerySchema>;
export type ListMemoriesResponse = z.infer<typeof ListMemoriesResponseSchema>;
export type GetMemoryReadCountsBody = z.infer<
  typeof GetMemoryReadCountsBodySchema
>;
export type MemoryReadCounts = z.infer<typeof MemoryReadCountsSchema>;
